import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ServiceProviderFilters } from '@client/components/mspr/ServiceProviderFilters';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useProviderTableColumns } from './columns';

export const Route = createFileRoute('/console/$orgId/mspr/service-providers/search')({
    staticData: {
        ui: {
            title: 'Service providers',
            subtitle: 'Manage and track your material service providers',
            widthVariant: 'fullWidth',
        },
    },
    component: ServiceProviders,
});

type QueryParams = RouterInputs['mspr']['msp']['listProviders'];

function ServiceProviders() {
    const orgId = useOrgId();
    const navigate = useNavigate();

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
    });

    const { data, isLoading, refetch } = trpc.mspr.msp.listProviders.useQuery(queryParams);

    const columns = useProviderTableColumns();

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading,
        columns,
        rowKey: (row) => String(row.id),
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'service-providers-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        showSorterTooltip: false,
        emptyState: { noRecordsTitle: 'No service providers yet' },
    });

    return (
        <PageController route={Route}>
            <Outlet />

            <TableToolbar
                filters={
                    <ServiceProviderFilters
                        urlKey="service-provider-filters"
                        onChange={(where) => {
                            setQueryParams((prev) => ({ ...prev, where, offset: 0 }));
                        }}
                    />
                }
                actions={
                    <Button.Add.Link
                        variant="primary"
                        to="/console/$orgId/mspr/service-providers/search/add"
                        params={{ orgId }}
                        label="Add service provider"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/mspr/service-providers/details/$id/manage',
                                params: { orgId, id },
                            },
                            e,
                        );
                    },
                })}
            />
        </PageController>
    );
}
